import moment from 'moment';
import escapeStringRegex from 'escape-string-regexp';
import Alerts from 'lib/alerts';
import modal from 'helpers/modal.js';
import loader from 'helpers/loader.js';
import FormView from 'commons/views/Form.js';
import DefaultPayrollDetailView from './DefaultPayrollDetail.js';
import Position from 'newModels/position.js';
import LeaveTypes from 'newCollections/leave-types.js';
import PayrollItemSelectionModal from
    'employer/views/PayrollItemSelectionModal.js';
import template from '../../templates/employee/EmploymentDetails.ejs';

module.exports = FormView.extend({
    availablePayrollItems: null,
    payrollItemsModal:     null,
    payrollDetailViews:    null,

    events: function() {
        return Object.assign({}, FormView.prototype.events(), {
            'typeahead:change .js-ext-position': 'updatePosition',
            'typeahead:select .js-ext-position': 'updatePosition',
            'keyup .js-ext-position':            'updatePosition',
            'change .js-ext-companyLocation':    'updateCompanyLocation',
            'click .js-add-payroll-items':       'showPayrollItemsModal',
            'change .js-ext-days':               'updateDays',
            'change .js-ext-managers':           'selectManagers',
            'change .js-ext-managers-l2':        'selectManagersL2',
            'change .js-ext-managers-l3':        'selectManagersL3',
            'change .js-ext-carryForward':       'updateCarryForward',
            'click .js-add-second-layer':        'showSecondApprovalLayer',
            'click .js-add-third-layer':         'showThirdApprovalLayer'
        });
    },

    initialize: function(options) {
        //Ideally we should add attribute in Item model to be filtered by gender
        const byGender = employee => type => {
            return !type.get('gender') ||
                type.get('gender') === employee.get('gender');
        };

        this.options = Object.assign({
            payrollDetails: null,
            positions: null,
            approverEmployees: null,
            leaveApprovals: null,
            locations: null,
            payrollItems: null,
            unsubmittedPayroll: null,
            leaveTypes: null,
            carryForwardDays: {original: {}},
            isNew: false,
            template: template
        }, options || {});

        this.selectedManagers = this.options.leaveApprovals
            .filter(a => a.get('layer') === 1).map(a => a.get('approverId'));
        this.selectedManagersL2 = this.options.leaveApprovals
            .filter(a => a.get('layer') === 2).map(a => a.get('approverId'));
        this.selectedManagersL3 = this.options.leaveApprovals
            .filter(a => a.get('layer') === 3).map(a => a.get('approverId'));
        this.options.templateExtra = {
            canCarryForwardDays: (
                app.company &&
                app.company.get('allowLeaveCarryForward')
            ),
            locations: this.options.locations,
            approverEmployees: this.options.approverEmployees,
            selectedManagers: this.selectedManagers,
            selectedManagersL2: this.selectedManagersL2,
            selectedManagersL3: this.selectedManagersL3,
            leaveTypes: this.options.leaveTypes.filter(byGender(this.model)),
            carryForwardDays: this.options.carryForwardDays,
            isNew: this.options.isNew,
            forceAllowJoinDateEdit: app.user.isAdmin()
        };

        this.availablePayrollItems = this.options.payrollItems.clone();
        this.payrollItemsModal = new PayrollItemSelectionModal({
            title: 'Add Monthly Payroll Item',
            text: '<strong>IMPORTANT</strong> Only add payroll items ' +
                'that are paid monthly as this will affect calculations!<br>',
            collection: this.availablePayrollItems
        });
        this.updateAvailablePayrollItems();

        this.payrollDetailViews = [];

        this.constraints = {
            commencementDate: {
                presence: {message: '^Join Date cannot be blank'}
            },
            positionId: {presence: {message: '^Position cannot be blank'}},

            /* NOTE that there already exists a constraint on earningsFrequency
             * on the constraints object. This one doesn't override the other,
             * both constraints are run.
             * */
            earningsFrequency: {presence: true},
            earningsAmount: {
                presence: {message: '^Earnings cannot be blank'},
                numericality: {
                    greaterThanOrEqualTo: 0,
                    message: '^Earnings must be a positive number'
                }
            }
        };
        return FormView.prototype.initialize.apply(this, [this.options]);
    },

    bindHandlers: function() {
        var r = FormView.prototype.bindHandlers.apply(this, arguments);

        this.options.payrollDetails.on(
            'add remove sync', this.updateAvailablePayrollItems, this
        );
        this.options.payrollDetails.on(
            'add remove sync', this.renderPayrollDetails, this
        );

        this.payrollItemsModal.on('select', this.addPayrollDetails, this);

        return r;
    },

    unbindHandlers: function() {
        var r = FormView.prototype.unbindHandlers.apply(this, arguments);

        this.options.payrollDetails.off(null, null, this);
        this.payrollItemsModal.off(null, null, this);

        return r;
    },

    render: function() {
        var r;

        this.options.templateExtra.leaveTakenPeriod =
            this.getLeaveTakenPeriod();
        this.options.templateExtra.selectedManagers =
            this.selectedManagers;
        this.options.templateExtra.selectedManagersL2 =
            this.selectedManagersL2;
        this.options.templateExtra.selectedManagersL3 =
            this.selectedManagersL3;

        r = FormView.prototype.render.apply(this, arguments);

        this.initManagerSelectors();
        if (this.selectedManagersL2.length > 0) {
            this.showSecondApprovalLayer();
        }
        if (this.selectedManagersL3.length > 0) {
            this.showThirdApprovalLayer();
        }
        this.initPositionInput();
        this.renderPayrollDetails();

        this.model.setAdditionalConstraints(null);
        this.markMandatoryFields(this.constraints);

        return r;
    },

    getLeaveTakenPeriod: function() {
        const renewalPolicy = app.company.get('leaveRenewalPolicy') || 'year';
        let dateFormat = 'MMM DD, YYYY';
        if (renewalPolicy === 'joinDate') {
            const joinDate = moment(
                this.model.get('commencementDate') || Date.now()
            );
            if (joinDate.day() === 1 && joinDate.month() === 0) {
                dateFormat = 'MMM DD, YYYY';
            } else {
                dateFormat = 'MMM YYYY';
            }
            joinDate.year(moment().year());
            if (joinDate.isAfter(moment())) {
                joinDate.year(moment().year() - 1);
            }
            return {
                start: joinDate.format(dateFormat),
                end: joinDate.add(1, 'year').format(dateFormat)
            };
        }
        return {
            start: moment().startOf('year').format(dateFormat),
            end: moment().endOf('year').format(dateFormat)
        };
    },

    initPositionInput: function() {
        this.$('.js-ext-position').typeahead({
            hint:      true,
            highlight: true,
            minLength: 0
        }, {
            name: 'positions',
            display: function(position) { return position.get('name'); },
            source: function(q, callback) {
                var matches = [];
                var regex = new RegExp(escapeStringRegex(q), 'i');

                this.options.positions.each(function(position) {
                    if (regex.test(position.get('name'))) {
                        matches.push(position);
                    }
                });

                callback(matches);
            }.bind(this)
        });
    },

    renderPayrollDetails: function() {
        const sortByName = detail => detail.getPayrollItem().get('name');
        const $el = this.$('.js-payroll-details-list').html('');
        const renderableDetails = _.sortBy(this.options.payrollDetails.filter(
            model => model.getPayrollItem().isSelectableAsDefault()
        ), sortByName);

        const isEmptyList = (renderableDetails.length === 0);

        this.payrollDetailViews.forEach(view => { view.remove(); });
        this.payrollDetailViews = [];

        renderableDetails.forEach(function(model) {
            const view = new DefaultPayrollDetailView({model: model});
            $el.append(view.$el).removeClass('hidden');
            this.payrollDetailViews.push(view);
        }.bind(this));

        this.$('.js-payroll-details-list').toggleClass('hidden', isEmptyList);
        this.$('.js-payroll-details-empty').toggleClass('hidden', !isEmptyList);
    },

    updatePosition: function(ev) {
        const q = this.$(ev.currentTarget).val();

        /* Ensure an empty string is not entered */
        if (!q) {
            this.model.setPosition(null);
            return;
        }
        const regex = new RegExp('^' + escapeStringRegex(q) + '$', 'i');
        let position = this.options.positions.find(position => {
            return regex.test(position.get('name') || '');
        });

        if (!position) {
            position = new Position({
                companyId: this.model.get('companyId'),
                name: q
            });
        }
        this.model.setPosition(position);
    },

    updateCompanyLocation: function(ev) {
        const $el = this.$(ev.currentTarget);
        const companyLocation = this.options.locations.get($el.val());

        this.model.set('companyLocationId', companyLocation.id);
    },

    initManagerSelectors: function() {
        this.selectedManagers.forEach(id => {
            if (this.$(`.js-ext-managers-l2 option[value='${id}']`)) {
                this.$(`.js-ext-managers-l2 option[value='${id}']`)
                    .hide();
            }
            if (this.$(`.js-ext-managers-l3 option[value='${id}']`)) {
                this.$(`.js-ext-managers-l3 option[value='${id}']`)
                    .hide();
            }
        });
        this.selectedManagersL2.forEach(id => {
            if (this.$(`.js-ext-managers option[value='${id}']`)) {
                this.$(`.js-ext-managers option[value='${id}']`)
                    .hide();
            }
            if (this.$(`.js-ext-managers-l3 option[value='${id}']`)) {
                this.$(`.js-ext-managers-l3 option[value='${id}']`)
                    .hide();
            }
        });
        this.selectedManagersL3.forEach(id => {
            if (this.$(`.js-ext-managers option[value='${id}']`)) {
                this.$(`.js-ext-managers option[value='${id}']`)
                    .hide();
            }
            if (this.$(`.js-ext-managers-l2 option[value='${id}']`)) {
                this.$(`.js-ext-managers-l2 option[value='${id}']`)
                    .hide();
            }
        });
        this.$('.js-ext-managers')
            .trigger('chosen:updated');
        this.$('.js-ext-managers-l2')
            .trigger('chosen:updated');
        this.$('.js-ext-managers-l3')
            .trigger('chosen:updated');
    },

    selectManagers: function(ev) {
        const $select = this.$(ev.currentTarget);
        const selected = $select.val();
        if (selected.length > 0) {
            if (this.$('.js-second-layer').prop('style').display === 'none') {
                this.$('.js-add-second-layer').show();
            } else {
                this.$('.js-ext-managers-l2')
                    .prop('disabled', false)
                    .trigger('chosen:updated');
            }
        } else {
            if (this.$('.js-second-layer').prop('style').display === 'none') {
                this.$('.js-add-second-layer').hide();
            } else {
                this.$('.js-ext-managers-l2')
                    .prop('disabled', true)
                    .trigger('chosen:updated');
            }
        }

        const toRemove = this.selectedManagers
            .find(m => selected.indexOf(m) < 0);
        if (toRemove) {
            const removeModel = this.options.leaveApprovals.models
                .find(a => toRemove === a.get('approverId'));
            this.options.leaveApprovals.remove(removeModel);
            if (this.$(`.js-ext-managers-l2 option[value='${toRemove}']`)) {
                this.$(`.js-ext-managers-l2 option[value='${toRemove}']`)
                    .show();
                this.$('.js-ext-managers-l2')
                    .trigger('chosen:updated');
            }
            if (this.$(`.js-ext-managers-l3 option[value='${toRemove}']`)) {
                this.$(`.js-ext-managers-l3 option[value='${toRemove}']`)
                    .show();
                this.$('.js-ext-managers-l3')
                    .trigger('chosen:updated');
            }
        } else {
            const toAdd = selected
                .find(m => this.selectedManagers.indexOf(m) < 0);
            this.options.leaveApprovals.add({
                requesterId: this.model.id,
                approverId: toAdd,
                layer: 1
            }, {validate: false});
            if (this.$(`.js-ext-managers-l2 option[value='${toAdd}']`)) {
                this.$(`.js-ext-managers-l2 option[value='${toAdd}']`)
                    .hide();
                this.$('.js-ext-managers-l2')
                    .trigger('chosen:updated');
            }
            if (this.$(`.js-ext-managers-l3 option[value='${toAdd}']`)) {
                this.$(`.js-ext-managers-l3 option[value='${toAdd}']`)
                    .hide();
                this.$('.js-ext-managers-l3')
                    .trigger('chosen:updated');
            }
        }
        this.selectedManagers = selected;
    },

    showSecondApprovalLayer: function() {
        this.$('.js-second-layer').show();
        this.$('.js-add-second-layer').hide();
    },

    selectManagersL2: function(ev) {
        const $select = this.$(ev.currentTarget);
        const selected = $select.val();
        if (selected.length > 0) {
            if (this.$('.js-third-layer').prop('style').display === 'none') {
                this.$('.js-add-third-layer').show();
            } else {
                this.$('.js-ext-managers-l3')
                    .prop('disabled', false)
                    .trigger('chosen:updated');
            }
        } else {
            if (this.$('.js-third-layer').prop('style').display === 'none') {
                this.$('.js-add-third-layer').hide();
            } else {
                this.$('.js-ext-managers-l3')
                    .prop('disabled', true)
                    .trigger('chosen:updated');
            }
        }

        const toRemove = this.selectedManagersL2
            .find(m => selected.indexOf(m) < 0);
        if (toRemove) {
            const removeModel = this.options.leaveApprovals.models
                .find(a => toRemove === a.get('approverId'));
            this.options.leaveApprovals.remove(removeModel);
            if (this.$(`.js-ext-managers-l3 option[value='${toRemove}']`)) {
                this.$(`.js-ext-managers-l3 option[value='${toRemove}']`)
                    .show();
                this.$('.js-ext-managers-l3')
                    .trigger('chosen:updated');
            }
            if (this.$(`.js-ext-managers option[value='${toRemove}']`)) {
                this.$(`.js-ext-managers option[value='${toRemove}']`)
                    .show();
                this.$('.js-ext-managers')
                    .trigger('chosen:updated');
            }
        } else {
            const toAdd = selected
                .find(m => this.selectedManagersL2.indexOf(m) < 0);
            this.options.leaveApprovals.add({
                requesterId: this.model.id,
                approverId: toAdd,
                layer: 2
            }, {validate: false});
            if (this.$(`.js-ext-managers option[value='${toAdd}']`)) {
                this.$(`.js-ext-managers option[value='${toAdd}']`)
                    .hide();
                this.$('.js-ext-managers')
                    .trigger('chosen:updated');
            }
            if (this.$(`.js-ext-managers-l3 option[value='${toAdd}']`)) {
                this.$(`.js-ext-managers-l3 option[value='${toAdd}']`)
                    .hide();
                this.$('.js-ext-managers-l3')
                    .trigger('chosen:updated');
            }
        }
        this.selectedManagersL2 = selected;
    },

    showThirdApprovalLayer: function() {
        this.$('.js-third-layer').show();
        this.$('.js-add-third-layer').hide();
    },

    selectManagersL3: function(ev) {
        const $select = this.$(ev.currentTarget);
        const selected = $select.val();

        const toRemove = this.selectedManagersL3
            .find(m => selected.indexOf(m) < 0);
        if (toRemove) {
            const removeModel = this.options.leaveApprovals.models
                .find(a => toRemove === a.get('approverId'));
            this.options.leaveApprovals.remove(removeModel);
            if (this.$(`.js-ext-managers option[value='${toRemove}']`)) {
                this.$(`.js-ext-managers option[value='${toRemove}']`)
                    .show();
                this.$('.js-ext-managers')
                    .trigger('chosen:updated');
            }
            if (this.$(`.js-ext-managers-l2 option[value='${toRemove}']`)) {
                this.$(`.js-ext-managers-l2 option[value='${toRemove}']`)
                    .show();
                this.$('.js-ext-managers-l2')
                    .trigger('chosen:updated');
            }
        } else {
            const toAdd = selected
                .find(m => this.selectedManagersL3.indexOf(m) < 0);
            this.options.leaveApprovals.add({
                requesterId: this.model.id,
                approverId: toAdd,
                layer: 3
            }, {validate: false});
            if (this.$(`.js-ext-managers option[value='${toAdd}']`)) {
                this.$(`.js-ext-managers option[value='${toAdd}']`)
                    .hide();
                this.$('.js-ext-managers')
                    .trigger('chosen:updated');
            }
            if (this.$(`.js-ext-managers-l2 option[value='${toAdd}']`)) {
                this.$(`.js-ext-managers-l2 option[value='${toAdd}']`)
                    .hide();
                this.$('.js-ext-managers-l2')
                    .trigger('chosen:updated');
            }
        }
        this.selectedManagersL3 = selected;
    },

    validateApprovals: function() {
        const noDuplicatesList =
            [...new Set(this.options.leaveApprovals.models)];
        if (this.options.leaveApprovals.length > noDuplicatesList) {
            throw new Error(
                'Note: The same manager cannot be assigned twice.'
            );
        }
        const layer1 = this.options.leaveApprovals.filter(
            approval => approval.get('layer') === 1
        );
        const layer2 = this.options.leaveApprovals.filter(
            approval => approval.get('layer') === 2
        );
        const layer3 = this.options.leaveApprovals.filter(
            approval => approval.get('layer') === 3
        );
        if (
            (layer3.length > 0 &&
                (layer2.length === 0 || layer1.length === 0)) ||
            (layer2.length > 0 && layer1.length === 0)
        ) {
            throw new Error(
                'Note: A lower approval layer cannot be empty when a ' +
                'higher layer is already populated.'
            );
        }
    },

    showPayrollItemsModal: function(e) {
        e.preventDefault();
        this.payrollItemsModal.show();
    },

    updateAvailablePayrollItems: function() {
        this.availablePayrollItems.reset(
            this.options.payrollItems.clone().filter(model => {
                return !(
                    this.options.payrollDetails.find(detail => {
                        const payrollItem = detail.getPayrollItem();
                        return (payrollItem && payrollItem.id === model.id);
                    })
                );
            })
        );
    },

    addPayrollDetails: function(payrollItems, additionalInfo) {
        payrollItems.forEach(payrollItem => {
            const info = _.isObject(additionalInfo) ?
                (additionalInfo[payrollItem.id] || null) : null;

            this.options.payrollDetails.add({
                employeeId: this.model.id,
                payrollItemId: payrollItem.id,
                companyId: app.company.id,
                rate: payrollItem.get('isUnit') ? 1 : null,
                unit: payrollItem.get('isUnit') ? 0 : null,
                additionalInformation: info,
                isAffectOvertime: payrollItem.get('defaultIsAffectOvertime') ||
                    false
            });
        });
    },

    resetPayrollConfirm: function() {
        const resolver = Promise.defer();

        if (!_.isObject(this.options.unsubmittedPayroll)) {
            return Promise.resolve(true);
        }

        Alerts.show('infoConfirm', {
            title: 'Your saved payroll will be modified!',
            text:  'You have an unsubmitted payroll saved. ' +
                'If you continue, the payroll details of this employee will ' +
                'be updated based on the modifications you are about to ' +
                'save. Also, any unsaved payroll details will be lost.',
            confirmButtonText:  'Yes, Save Employment Details',
            callback: confirm => {
                if (confirm) {
                    resolver.resolve(true);
                } else {
                    resolver.reject();
                }
            }
        });

        return resolver.promise;
    },

    dirty: function() {
        return (
            this.model.dirty() ||
            this.options.payrollDetails.dirty() ||
            this.options.leaveApprovals.dirty() ||
            (
                this.model.getPosition() &&
                    this.model.getPosition().dirty()
            ) ||
            (this.model.getCompanyLocation() &&
                this.model.getCompanyLocation().dirty())
        );
    },

    save: function(ev) {
        const $button = this.$(ev.currentTarget);
        let stopLoader = () => {};

        return this.resetPayrollConfirm()
            .then(() => {
                this.model.setAdditionalConstraints(this.constraints);
                stopLoader = loader.startFullScreen($button);
                this.validateApprovals();

                return this.model.save().catch(err => {
                    if (this.model.wasValid()) {
                        // the error is not a validation error
                        throw err;
                    }
                    throw new Error('validationError');
                });
            })
            .then(() => Promise.all([
                this.options.payrollDetails.save(),
                this.options.leaveApprovals.save(),
                this.options.leaveTypes.fetchBalances(
                    this.model, // employee
                    moment().tz('Asia/Kuala_Lumpur')
                        .endOf('day').toDate()
                ),
            ])).then(() => this.options.unsubmittedPayroll ?
                this.options.unsubmittedPayroll
                    .resetEmployee({employee: this.model}) :
                Promise.resolve())
            .then(() => {
                if (!this.options.isNew) {
                    this.render();
                    modal.showAlertSuccess(
                        'Employment details have been saved'
                    );
                    return Promise.resolve(true);
                }

                if (
                    app.company.get('employeeCount') === 1 &&
                    this.options.isNew
                ) {
                    Alerts.show('success', {
                        text: 'Employment details have been saved, proceed ' +
                            'to statutory requirements.',
                        callback: () => app.mainRouter.navigate(
                            'employee/' + this.model.id +
                            '/statutoryRequirements/isNew',
                            {trigger: true, replace: true}
                        )
                    });
                } else if (this.options.isNew) {
                    app.mainRouter.navigate(
                        'employee/' + this.model.id +
                            '/statutoryRequirements/isNew',
                        {trigger: true, replace: true}
                    );
                }
            })
            .catch(err => {
                if (
                    typeof err === 'object' &&
                    err.message &&
                    err.message.startsWith('Note:')
                ) {
                    Alerts.show('error', {
                        title: 'Incorrect approval setup.',
                        text: err.message
                    });
                } else if (/Payroll Item already exist for payroll/.test(err.message)) {
                    Alerts.show('warning', {
                        text:'You can\'t submit same employee details more than once at the same time.',
                        customClass: 'sweet-alert-error'
                    });
                } else if (
                    typeof err === 'object' &&
                    err.message !== 'validationError'
                ) {
                    Alerts.show('criticalError', err);
                }
            })
            .finally(() => {
                this.model.setAdditionalConstraints(null);
                stopLoader();
            });
    },

    fetch: function() {
        /* Fetch carry forward days before model is updated as that will
           trigger a screen refresh */
        return LeaveTypes.fetchCarryForwardDays(this.model)
            .then(carryForwardDays => {
                this.options.carryForwardDays = carryForwardDays;
                this.options.templateExtra.carryForwardDays = carryForwardDays;
                return Promise.all([
                    this.model.fetch(),
                    this.options.payrollDetails.fetch()
                ]);
            });

    },

    updateDays: function($ev) {
        const typeId = this.$($ev.currentTarget).data('type-id');
        const days = this.getInputValue(this.$($ev.currentTarget));
        this.model.setDaysEntitled({
            typeId: typeId,
            days: days
        });
    },

    updateCarryForward: function($ev) {
        const typeId = this.$($ev.currentTarget).data('type-id');
        const days = this.getInputValue(this.$($ev.currentTarget));
        if (days < 0) {
            this.$($ev.currentTarget).val(0);
            return;
        }
        this.model.setDaysCarryForward({
            typeId: typeId,
            days: days
        });
        this.options.carryForwardDays[typeId] = days;
    }
});



// WEBPACK FOOTER //
// ./app/employer/views/employee/EmploymentDetails.js